<template>
	<a
		:href="link"
		class="event-list-item"
		:target="isExternal ? '_blank' : null"
		:rel="isExternal ? 'noopener noreferrer' : null"
	>
		<EventListItemTime
			class="event-list-item__time"
			:class="{'-volley': isVolleyVariant}"
			:time="time"
		>
			<div
				v-if="contextLabel || competitionStageInfo || teamCategoryLabel"
				class="event-list-item__metadata"
				:class="isVolleyVariant ? '-volley' : '-mobile-header'"
			>
				<span
					v-if="contextLabel"
					class="event-list-item__metadata-item"
				>{{ contextLabel }}</span>
				<span
					v-if="competitionStageInfo"
					class="event-list-item__metadata-item"
				>{{ competitionStageInfo.label }}</span>
				<span
					v-if="teamCategoryLabel"
					class="event-list-item__metadata-item"
				>{{ teamCategoryLabel }}</span>
			</div>
		</EventListItemTime>

		<div
			class="event-list-item__content"
			:class="{'-match': isMatchVariant}"
		>
			<div
				v-if="!isVolleyVariant && (contextLabel || competitionStageInfo || teamCategoryLabel)"
				class="event-list-item__metadata -desktop-content"
			>
				<span
					v-if="contextLabel"
					class="event-list-item__metadata-item"
				>{{ contextLabel }}</span>
				<span
					v-if="competitionStageInfo"
					class="event-list-item__metadata-item"
				>{{ competitionStageInfo.label }}</span>
				<span
					v-if="teamCategoryLabel"
					class="event-list-item__metadata-item"
				>{{ teamCategoryLabel }}</span>
			</div>

			<EventListItemVolleyContent
				v-if="isVolleyVariant"
				class="event-list-item__title"
				:home-name="homeName"
				:home-name-full="homeNameFull"
				:home-img-url="homeImgUrl"
				:home-score-with-fallback="homeScoreWithFallback"
				:guest-name="guestName"
				:guest-name-full="guestNameFull"
				:guest-img-url="guestImgUrl"
				:guest-score-with-fallback="guestScoreWithFallback"
				:number-of-periods="numberOfPeriods"
				:scores="scores"
				:no-spoiler-mode="noSpoilerMode"
				:status="status"
			/>

			<EventListItemMatchContent
				v-else-if="isMatchVariant"
				class="event-list-item__title"
				:home-name="homeName"
				:home-name-full="homeNameFull"
				:home-img-url="homeImgUrl"
				:home-score-with-fallback="homeScoreWithFallback"
				:guest-name="guestName"
				:guest-name-full="guestNameFull"
				:guest-img-url="guestImgUrl"
				:guest-score-with-fallback="guestScoreWithFallback"
				:score-extra-abbr="scoreExtraAbbr"
				:no-spoiler-mode="noSpoilerMode"
				:dash-score-devider="dashScoreDevider"
			/>

			<EventListItemSingleContent
				v-else
				class="event-list-item__title"
				:title="title"
				:subtitle="subtitle"
				:sport-label="sportLabel"
				:discipline-label="disciplineLabel"
				:meta-items="metaItems"
				:participants="participants"
			/>
		</div>

		<div class="event-list-item__trailing">
			<EventListItemStatusBadge
				v-if="statusBadgeStatus"
				class="event-list-item__status"
				:status="statusBadgeStatus"
			/>
			<EventListItemActions
				class="event-list-item__actions"
				:is-external="isExternal"
			/>
		</div>
	</a>
</template>

<script>
import { getDateTime, getRelativeTime } from '@shared/utils/timeUtils';
import { getEventStatus, EVENT_STATUS } from '@shared/utils/eventUtils';
import { EventType } from '@shared/enums/EventType';
import { mapState } from 'vuex';
import EventListItemActions from './EventListItemActions.vue';
import EventListItemMatchContent from './EventListItemMatchContent.vue';
import EventListItemSingleContent from './EventListItemSingleContent.vue';
import EventListItemStatusBadge from './EventListItemStatusBadge.vue';
import EventListItemTime from './EventListItemTime.vue';
import EventListItemVolleyContent from './EventListItemVolleyContent.vue';

export default {
	name:       'EventListItem',
	components: {
		EventListItemActions,
		EventListItemMatchContent,
		EventListItemSingleContent,
		EventListItemStatusBadge,
		EventListItemTime,
		EventListItemVolleyContent,
	},
	props: {
		variant: {
			type:      String,
			required:  true,
			validator: value => ['volley', 'match', 'single'].includes(value),
		},
		title: {
			type:    String,
			default: null,
		},
		subtitle: {
			type:    String,
			default: null,
		},
		sportLabel: {
			type:    String,
			default: null,
		},
		contextLabel: {
			type:    String,
			default: null,
		},
		teamCategoryLabel: {
			type:    String,
			default: null,
		},
		disciplineLabel: {
			type:    String,
			default: null,
		},
		metaItems: {
			type:    Array,
			default: () => [],
		},
		participants: {
			type:    Array,
			default: () => [],
		},
		homeName: {
			type:    String,
			default: null,
		},
		homeNameFull: {
			type:    String,
			default: null,
		},
		homeImgUrl: {
			type:    String,
			default: null,
		},
		homeScore: {
			type:    String,
			default: null,
		},
		guestName: {
			type:    String,
			default: null,
		},
		guestNameFull: {
			type:    String,
			default: null,
		},
		guestImgUrl: {
			type:    String,
			default: null,
		},
		guestScore: {
			type:    String,
			default: null,
		},
		numberOfPeriods: {
			type:    Number,
			default: 0,
		},
		scores: {
			type:    Array,
			default: () => [],
		},
		scoreExtraAbbr: {
			type:    String,
			default: null,
		},
		link: {
			type:     String,
			required: true,
		},
		unixStart: {
			type:     Number,
			required: true,
		},
		unixKickoff: {
			type:     Number,
			required: true,
		},
		unixEnd: {
			type:     Number,
			required: true,
		},
		unixNow: {
			type:     Number,
			required: true,
		},
		eventType: {
			type:     String,
			required: true,
		},
		isExternal: {
			type:    Boolean,
			default: false,
		},
		competitionStageInfo: {
			type:    Object,
			default: null,
		},
		dashScoreDevider: {
			type:    Boolean,
			default: false,
		},
	},
	computed: {
		...mapState({
			preferences: state => state.user.preferences,
		}),
		noSpoilerMode() {
			return this.preferences?.noSpoilerMode || false;
		},
		status() {
			return getEventStatus(this.eventType, this.unixStart, this.unixEnd, this.unixNow);
		},
		statusBadgeStatus() {
			return this.eventType === EventType.EVENT ? this.status : null;
		},
		time() {
			switch (this.status) {
			case EVENT_STATUS.UPCOMING:
				return getDateTime(this.unixStart, { withDate: false });
			case EVENT_STATUS.LIVE:
				return getRelativeTime(this.unixEnd, { useRemaining: true, unixNow: this.unixNow });
			default:
				return getDateTime(this.unixKickoff, { withDate: false });
			}
		},
		homeScoreWithFallback() {
			return this.homeScore || (this.status === EVENT_STATUS.LIVE ? '0' : '');
		},
		guestScoreWithFallback() {
			return this.guestScore || (this.status === EVENT_STATUS.LIVE ? '0' : '');
		},
		isVolleyVariant() {
			return this.variant === 'volley';
		},
		isMatchVariant() {
			return this.variant === 'match';
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.event-list-item {
	position: relative;
	display: grid;
	grid-template-areas:
		'time status'
		'content actions';
	grid-template-columns: minmax(0, 1fr) auto;
	row-gap: $sp4;
	column-gap: $sp4;
	margin-bottom: $sp3;
	padding-top: $sp4;
	padding-right: $sp4;
	padding-bottom: $sp4;
	padding-left: $sp4;
	border-width: 1px;
	border-style: solid;
	border-color: var(--color-grey-800);
	border-radius: var(--t-border-radius-card--lg);
	background-color: var(--color-grey-950);
	color: var(--color-white);
	overflow: hidden;
	transition-property: background-color, border-color;
	transition-duration: $trans-time-fast;

	&:focus,
	&:hover {
		background-color: var(--color-grey-900);
	}

	&:focus-visible {
		outline-width: 2px;
		outline-style: solid;
		outline-color: var(--color-hype-yellow);
		outline-offset: 2px;
	}

	@supports (background-color: color-mix(in srgb, black, white)) {
		--event-list-item-background-color: color-mix(in srgb, var(--color-body-background) 97%, var(--color-white));
		--event-list-item-border-color: color-mix(in srgb, var(--color-body-background) 84%, var(--color-white));

		border-color: var(--event-list-item-border-color);
		background-color: var(--event-list-item-background-color);

		&:focus,
		&:hover {
			--event-list-item-background-color: color-mix(in srgb, var(--color-body-background) 94%, var(--color-white));
		}
	}

	body.-t-light & { // stylelint-disable-line selector-no-qualifying-type
		border-color: var(--color-grey-900);
		background-color: rgba(var(--color-rgb-black), 0.02); // stylelint-disable-line declaration-property-value-allowed-list
		color: var(--color-black);

		&:focus,
		&:hover {
			background-color: var(--color-grey-950);
		}

		@supports (background-color: color-mix(in srgb, black, white)) {
			--event-list-item-background-color: color-mix(in srgb, var(--color-body-background) 97%, var(--color-black));
			--event-list-item-border-color: color-mix(in srgb, var(--color-body-background) 84%, var(--color-black));

			&:focus,
			&:hover {
				--event-list-item-background-color: color-mix(in srgb, var(--color-body-background) 94%, var(--color-black));
			}
		}
	}

	@include size(md-up) {
		grid-template-areas: 'time content trailing';
		grid-template-columns: 136px minmax(0, 1fr) 136px;
		align-items: center;
		row-gap: $sp5;
		column-gap: $sp5;
		min-height: 80px;
		padding-top: $sp3;
		padding-right: $sp5;
		padding-bottom: $sp3;
		padding-left: $sp5;
	}
}

.event-list-item__content {
	display: flex;
	grid-area: content;
	min-width: 0;
	flex-direction: column;
	row-gap: $sp1;

	&.-match {
		grid-column: 1 / -1;

		@include size(md-up) {
			grid-column: auto;
		}
	}
}

.event-list-item__title {
	min-width: 0;
	width: 100%;
}

.event-list-item__time {
	grid-area: time;
}

.event-list-item__metadata {
	@include font(primary, book, normal, fs-60);
	display: flex;
	min-width: 0;
	color: var(--color-grey-400);
	row-gap: $sp2;
	column-gap: $sp2;
	flex-wrap: wrap;
	justify-content: center;

	&.-mobile-header {
		width: 100%;
		text-align: left;
		justify-content: flex-start;

		@include size(md-up) {
			display: none;
		}
	}

	&.-desktop-content {
		display: none;

		@include size(md-up) {
			display: flex;
			text-align: center;
			justify-content: center;
		}
	}

	&.-volley {
		width: 100%;
		text-align: left;
		overflow-wrap: anywhere;
		justify-content: flex-start;

		@include size(md-up) {
			padding-right: $sp2;
			padding-left: $sp2;
			text-align: center;
			justify-content: center;
		}
	}
}

.event-list-item__metadata-item + .event-list-item__metadata-item::before {
	@include size(md-up) {
		margin-right: $sp2;
		content: '·';
	}
}

.event-list-item__trailing {
	display: contents;

	@include size(md-up) {
		display: flex;
		grid-area: trailing;
		align-items: center;
		align-self: center;
		column-gap: $sp3;
		justify-content: flex-end;
	}
}

.event-list-item__status {
	grid-area: status;
	align-self: start;
	justify-self: end;

	@include size(md-up) {
		grid-area: auto;
		align-self: auto;
		justify-self: auto;
	}
}

.event-list-item__actions {
	display: flex;
	grid-area: actions;
	align-self: center;
	justify-self: end;

	@include size(md-up) {
		grid-area: auto;
		align-self: auto;
		justify-self: auto;
	}
}
</style>
